import { ApplicantTable, Collection, CustomQuery, DashboardComponent, DashboardExplanation, Field, FieldEquals, FieldExists, PaymentModule, When } from "../../survey";

export const generatePaymentDashboards = (config: PaymentModule): Collection => {
  return {
    "kind": "Collection",
    "name": {
      "en": "Dashboards"
    },
    "options": {},
    "components": [
      ...[generateFinancialReportingDashboards(config)],
      ...[generatePaymentAdminDashboards(config)],
    ]
  };
};

const CURRENT_YEAR = new Date().getFullYear();

const generateFinancialReportingDashboards = (config: PaymentModule): Collection => ({
  "kind": "Collection",
  "name": {
    "en": "Financial Reporting"
  },
  "options": {},
  "components": [
    {
      "kind": "Dashboard",
      "title": "Financial Reporting Dashboard",
      "path": "financial",
      "components": [
        {
          "kind": "Custom Query",
          "sql": `WITH entries AS (\n    SELECT \n        (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END) AS booked_date,\n        action.created AS action_created_utc, \n        action.id AS action_id, \n        action.kind AS action_kind, \n        action.state AS action_state,\n        entry.action_part AS entry_role, \n        COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference) AS action_reference,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 1) AS program,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 2) AS uid,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 3) AS payment_name,\n        entry.amount / -100.00 AS booked_amount, \n        ledger.name AS ledger, \n        oppledger.name AS opposite_ledger, \n        CASE WHEN transaction_ids IS NOT NULL AND transaction_amount = entry_amount THEN true ELSE null END AS reconciled,\n        transaction.kind AS transaction_type, \n        transaction.created_date AS transaction_date, \n        transaction.external_reference AS transaction_ref, \n        transaction.amount AS transaction_amount, \n        transaction.state AS transaction_state,\n        COALESCE(usio_transaction.to_card_id, action.dynamic_metadata->>'cardId') AS to_card_id,\n        usio_transaction.usio_kind\n    FROM rc_public.entry \n    JOIN rc_public.action ON entry.action_id = action.id\n    JOIN rc_public.ledger ON entry.ledger_id = ledger.id\n    JOIN rc_public.entry opp ON opp.id = entry.opposite_entry\n    JOIN rc_public.ledger oppledger ON opp.ledger_id = oppledger.id\n    LEFT JOIN rc_public.approval ON approval.action_id = action.id\n    LEFT JOIN rc_public.entry_reconciliation_cached ON entry.id = entry_id\n    LEFT JOIN rc_public.transaction ON transaction.id = ANY(transaction_ids)\n    LEFT JOIN (SELECT transaction_id, array_agg(message) AS notes FROM rc_public.transaction_comment GROUP BY transaction_id) notes ON notes.transaction_id = transaction.id\n    LEFT JOIN (SELECT action_id, array_agg(message) AS notes FROM rc_public.action_comment GROUP BY action_id) anotes ON anotes.action_id = action.id\n    LEFT JOIN rc_public.usio_transaction ON usio_transaction.transaction_id = transaction.id\n    LEFT JOIN rc_public.load_usio_card ON load_usio_card.action_id = action.id\n    LEFT JOIN rc_public.usio_mailed_grant_action ON usio_mailed_grant_action.action_id = action.id\n    LEFT JOIN rc_public.dwolla_float_ach_grant ON dwolla_float_ach_grant.action_id = action.id\n    WHERE entry.account_id IS NOT NULL AND \n    (\n        (ledger.name LIKE '%${config.program}%' OR oppledger.name LIKE '%${config.program}%')\n    )\n    AND (\n        entry.action_part = 'grant_out' AND\n        action.state = 'COMPLETE' AND\n        EXTRACT(YEAR FROM \n            (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END)\n        ) = ${CURRENT_YEAR}\n    )\n)\nSELECT \n    TO_CHAR(booked_date, 'Month') AS booked_month,\n    action_state,\n    SUM(booked_amount) AS total_booked_amount\nFROM entries \nGROUP BY TO_CHAR(booked_date, 'Month'), action_state\nORDER BY \n    EXTRACT(MONTH FROM MIN(booked_date));`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": `Completed Grants Out - Dollars - ${CURRENT_YEAR}`
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `SELECT \n    TO_CHAR(DATE_TRUNC('month', booked_date), 'Month') as booked_month,\n    COUNT(DISTINCT action_id) as unique_action_id_count\nFROM (\n    SELECT \n        CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END as booked_date,\n        action.id as action_id  -- Count action_id instead of uid\n    FROM rc_public.entry \n    JOIN rc_public.action on entry.action_id = action.id\n    JOIN rc_public.ledger on entry.ledger_id = ledger.id\n    JOIN rc_public.entry opp on opp.id = entry.opposite_entry\n    JOIN rc_public.ledger oppledger on opp.ledger_id = oppledger.id\n    LEFT JOIN rc_public.approval on approval.action_id = action.id\n    LEFT JOIN rc_public.transaction on transaction.id = entry.transaction_id  -- Use the correct column name\n    LEFT JOIN rc_public.usio_transaction on usio_transaction.transaction_id = transaction.id\n    LEFT JOIN rc_public.load_usio_card on load_usio_card.action_id = action.id\n    LEFT JOIN rc_public.usio_mailed_grant_action on usio_mailed_grant_action.action_id = action.id\n    LEFT JOIN rc_public.dwolla_float_ach_grant on dwolla_float_ach_grant.action_id = action.id\n    WHERE entry.account_id is not null \n    AND (ledger.name like '%${config.program}%' or oppledger.name like '%${config.program}%')\n    AND entry.action_part = 'grant_out'  -- Filter by entry_role\n    AND action.state = 'COMPLETE' -- Filter by action_state\n) AS subquery\nWHERE EXTRACT(YEAR FROM booked_date) = '${CURRENT_YEAR}' -- Filter by booked_date year\nGROUP BY DATE_TRUNC('month', booked_date)\nORDER BY DATE_TRUNC('month', booked_date);`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": `Completed Grants Out - Count - ${CURRENT_YEAR}`
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `WITH entries AS (\n    SELECT \n        (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END) AS booked_date,\n        action.created AS action_created_utc, \n        action.id AS action_id, \n        action.kind AS action_kind, \n        action.state AS action_state,\n        entry.action_part AS entry_role, \n        COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference) AS action_reference,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 1) AS program,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 2) AS uid,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 3) AS payment_name,\n        entry.amount / -100.00 AS booked_amount, \n        ledger.name AS ledger, \n        oppledger.name AS opposite_ledger, \n        CASE WHEN transaction_ids IS NOT NULL AND transaction_amount = entry_amount THEN true ELSE null END AS reconciled,\n        transaction.kind AS transaction_type, \n        transaction.created_date AS transaction_date, \n        transaction.external_reference AS transaction_ref, \n        transaction.amount AS transaction_amount, \n        transaction.state AS transaction_state,\n        COALESCE(usio_transaction.to_card_id, action.dynamic_metadata->>'cardId') AS to_card_id,\n        usio_transaction.usio_kind\n    FROM rc_public.entry \n    JOIN rc_public.action ON entry.action_id = action.id\n    JOIN rc_public.ledger ON entry.ledger_id = ledger.id\n    JOIN rc_public.entry opp ON opp.id = entry.opposite_entry\n    JOIN rc_public.ledger oppledger ON opp.ledger_id = oppledger.id\n    LEFT JOIN rc_public.approval ON approval.action_id = action.id\n    LEFT JOIN rc_public.entry_reconciliation_cached ON entry.id = entry_id\n    LEFT JOIN rc_public.transaction ON transaction.id = ANY(transaction_ids)\n    LEFT JOIN (SELECT transaction_id, array_agg(message) AS notes FROM rc_public.transaction_comment GROUP BY transaction_id) notes ON notes.transaction_id = transaction.id\n    LEFT JOIN (SELECT action_id, array_agg(message) AS notes FROM rc_public.action_comment GROUP BY action_id) anotes ON anotes.action_id = action.id\n    LEFT JOIN rc_public.usio_transaction ON usio_transaction.transaction_id = transaction.id\n    LEFT JOIN rc_public.load_usio_card ON load_usio_card.action_id = action.id\n    LEFT JOIN rc_public.usio_mailed_grant_action ON usio_mailed_grant_action.action_id = action.id\n    LEFT JOIN rc_public.dwolla_float_ach_grant ON dwolla_float_ach_grant.action_id = action.id\n    WHERE entry.account_id IS NOT NULL AND \n    (\n        (ledger.name LIKE '%${config.program}%' OR oppledger.name LIKE '%${config.program}%')\n    )\n    AND (\n        entry.action_part = 'grant_out' \n    )\n)\nSELECT entries.action_state, SUM(entries.booked_amount) AS total_booked_amount\nFROM entries \nGROUP BY entries.action_state;`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": "Grant Status"
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `WITH entries AS (\n    SELECT \n        (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END) AS booked_date,\n        action.created AS action_created_utc, \n        action.id AS action_id, \n        action.kind AS action_kind, \n        action.state AS action_state,\n        entry.action_part AS entry_role, \n        COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference) AS action_reference,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 1) AS program,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 2) AS uid,\n        SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 3) AS payment_name,\n        entry.amount / 100.00 AS booked_amount, \n        ledger.name AS ledger, \n        oppledger.name AS opposite_ledger, \n        CASE WHEN transaction_ids IS NOT NULL AND transaction_amount = entry_amount THEN true ELSE null END AS reconciled,\n        transaction.kind AS transaction_type, \n        transaction.created_date AS transaction_date, \n        transaction.external_reference AS transaction_ref, \n        transaction.amount AS transaction_amount, \n        transaction.state AS transaction_state,\n        COALESCE(usio_transaction.to_card_id, action.dynamic_metadata->>'cardId') AS to_card_id,\n        usio_transaction.usio_kind\n    FROM rc_public.entry \n    JOIN rc_public.action ON entry.action_id = action.id\n    JOIN rc_public.ledger ON entry.ledger_id = ledger.id\n    JOIN rc_public.entry opp ON opp.id = entry.opposite_entry\n    JOIN rc_public.ledger oppledger ON opp.ledger_id = oppledger.id\n    LEFT JOIN rc_public.approval ON approval.action_id = action.id\n    LEFT JOIN rc_public.entry_reconciliation_cached ON entry.id = entry_id\n    LEFT JOIN rc_public.transaction ON transaction.id = ANY(transaction_ids)\n    LEFT JOIN (SELECT transaction_id, array_agg(message) AS notes FROM rc_public.transaction_comment GROUP BY transaction_id) notes ON notes.transaction_id = transaction.id\n    LEFT JOIN (SELECT action_id, array_agg(message) AS notes FROM rc_public.action_comment GROUP BY action_id) anotes ON anotes.action_id = action.id\n    LEFT JOIN rc_public.usio_transaction ON usio_transaction.transaction_id = transaction.id\n    LEFT JOIN rc_public.load_usio_card ON load_usio_card.action_id = action.id\n    LEFT JOIN rc_public.usio_mailed_grant_action ON usio_mailed_grant_action.action_id = action.id\n    LEFT JOIN rc_public.dwolla_float_ach_grant ON dwolla_float_ach_grant.action_id = action.id\n    WHERE entry.account_id IS NOT NULL AND \n    (\n        (ledger.name LIKE '%${config.program}%' OR oppledger.name LIKE '%${config.program}%')\n    )\n    AND (\n        action.kind = 'grant_funding' AND\n        EXTRACT(YEAR FROM \n            (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END)\n        ) = ${CURRENT_YEAR}\n    )\n)\nSELECT \n    TO_CHAR(booked_date, 'Month') AS booked_month,\n    action_state,\n    SUM(booked_amount) AS total_booked_amount\nFROM entries \nGROUP BY TO_CHAR(booked_date, 'Month'), action_state\nORDER BY \n    EXTRACT(MONTH FROM MIN(booked_date));`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": `Grant Funds Received by Month - ${CURRENT_YEAR}`
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `with entries as (select * from rc_public.monthly_reconciliation_cached\nwhere (ledger like '%${config.program}%' or opposite_ledger like '%${config.program}%') \nAND (\n    (action_kind = 'grant_funding' and (action_state = 'COMPLETE' or action_state = 'ANTICIPATED'))\n)\n    \nORDER BY action_created_utc, action_id, action_kind, entry_role)\n\nselect sum(transaction_amount)/100 from entries;`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": "Total Grant Funds Received"
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `with entries as (SELECT \n    (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END) as booked_date,\n    action.created as action_created_utc, \n    action.id as action_id, \n    action.kind as action_kind, \n    action.state as action_state,\n    entry.action_part as entry_role, \n    COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference) as action_reference,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 1) AS program,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 2) AS uid,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 3) AS payment_name,\n    entry.amount/100.00 as booked_amount, \n    ledger.name as ledger, \n    oppledger.name as opposite_ledger, \n    CASE WHEN transaction_ids is not null and transaction_amount = entry_amount THEN true ELSE null END as reconciled,\n    transaction.kind as transaction_type, \n    transaction.created_date as transaction_date, \n    transaction.external_reference as transaction_ref, \n    transaction.amount as transaction_amount, \n    transaction.state as transaction_state,\n    COALESCE(usio_transaction.to_card_id, action.dynamic_metadata->>'cardId') as to_card_id,\n    usio_transaction.usio_kind\nFROM rc_public.entry \nJOIN rc_public.action on entry.action_id = action.id\nJOIN rc_public.ledger on entry.ledger_id = ledger.id\nJOIN rc_public.entry opp on opp.id = entry.opposite_entry\nJOIN rc_public.ledger oppledger on opp.ledger_id = oppledger.id\nLEFT JOIN rc_public.approval on approval.action_id = action.id\nLEFT JOIN rc_public.entry_reconciliation_cached on entry.id = entry_id\nLEFT JOIN rc_public.transaction on transaction.id = ANY(transaction_ids)\nLEFT JOIN (select transaction_id, array_agg(message) as notes from rc_public.transaction_comment group by transaction_id) notes on notes.transaction_id = transaction.id\nLEFT JOIN (select action_id, array_agg(message) as notes from rc_public.action_comment group by action_id) anotes on anotes.action_id = action.id\nLEFT JOIN rc_public.usio_transaction on usio_transaction.transaction_id = transaction.id\nLEFT JOIN rc_public.load_usio_card on load_usio_card.action_id = action.id\nLEFT JOIN rc_public.usio_mailed_grant_action on usio_mailed_grant_action.action_id = action.id\nLEFT JOIN rc_public.dwolla_float_ach_grant on dwolla_float_ach_grant.action_id = action.id\nWHERE entry.account_id is not null and \n(\n    (ledger.name like '%${config.program}%' or oppledger.name like '%${config.program}%')\n)\nAND (\n    (action.kind = 'grant_funding' and (action.state = 'COMPLETE' or action.state = 'ANTICIPATED'))\n    OR (entry.action_part = 'grant_out' and (action.state = 'COMPLETE' or action.state = 'PENDING'))\n)\n    \nORDER BY action.created, action.id, action.kind, entry.action_part)\n\nselect sum(booked_amount) from entries;`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": "Total Grant Funds Available"
            }
          }
        },
        {
          "kind": "Custom Query",
          "sql": `\n\nSELECT \n    (CASE WHEN entry.amount < 0 THEN COALESCE(approval.created, transaction.created_date) ELSE transaction.created_date END) as booked_date,\n    action.created as action_created_utc, \n    action.id as action_id, \n    action.kind as action_kind, \n    action.state as action_state,\n    entry.action_part as entry_role, \n    COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference) as action_reference,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 1) AS program,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 2) AS uid,\n    SPLIT_PART(COALESCE(dwolla_float_ach_grant.reference, load_usio_card.reference, usio_mailed_grant_action.reference, action.dynamic_reference), ':', 3) AS payment_name,\n    entry.amount/100.00 as booked_amount, \n    ledger.name as ledger,\n    -- CASE WHEN transaction_ids is not null and transaction_amount = entry_amount THEN true ELSE null END as reconciled,\n    transaction.kind as transaction_type, \n    transaction.created_date as transaction_date, \n    transaction.external_reference as transaction_ref, \n    transaction.amount as transaction_amount, \n    transaction.state as transaction_state,\n    COALESCE(usio_transaction.to_card_id, action.dynamic_metadata->>'cardId') as to_card_id,\n    usio_transaction.usio_kind\nFROM rc_public.entry \nJOIN rc_public.action on entry.action_id = action.id\nJOIN rc_public.ledger on entry.ledger_id = ledger.id\nJOIN rc_public.entry opp on opp.id = entry.opposite_entry\nJOIN rc_public.ledger oppledger on opp.ledger_id = oppledger.id\nLEFT JOIN rc_public.approval on approval.action_id = action.id\n\n-- LEFT JOIN rc_public.entry_reconciliation on entry.id = entry_id\n-- LEFT JOIN rc_public.transaction on transaction.id = ANY(transaction_ids)\n-- LEFT JOIN (select transaction_id, array_agg(message) as notes from rc_public.transaction_comment group by transaction_id) notes on notes.transaction_id = transaction.id\n\nLEFT JOIN (select action_id, array_agg(message) as notes from rc_public.action_comment group by action_id) anotes on anotes.action_id = action.id\n\nLEFT JOIN rc_public.load_usio_card on load_usio_card.action_id = action.id\nLEFT JOIN rc_public.usio_mailed_grant_action on usio_mailed_grant_action.action_id = action.id\nLEFT JOIN rc_public.dwolla_float_ach_grant on dwolla_float_ach_grant.action_id = action.id\n\nLEFT JOIN rc_public.usio_transaction on load_usio_card.reference = reverse(split_part(reverse(scraped_data->>'type'),' ',1))\nLEFT JOIN rc_public.dwolla_transaction on dwolla_float_ach_grant.issued_transaction_id::uuid = dwolla_transaction.transaction_id\nLEFT JOIN rc_public.transaction on (transaction.id = usio_transaction.transaction_id or transaction.id = dwolla_transaction.transaction_id)\n\nWHERE entry.account_id is not null and \n(\n    (ledger.name like '%${config.program}%' or oppledger.name like '%${config.program}%')\n)\nORDER BY action.created, action.id, action.kind, entry.action_part;`,
          "visualization": {
            "kind": "Table",
            "title": {
              "en": "Action Data"
            }
          }
        }
      ],
      "version": 1
    }
  ]
});

const generatePaymentAdminDashboards = (config: PaymentModule): Collection => {
  return {
    "kind": "Collection",
    "name": {
      "en": "Participant Payment Administration"
    },
    "options": {},
    "components": [
      {
        "kind": "Dashboard",
        "title": "Payment Management",
        "path": "payment-management",
        "components": [
          {
            "kind": "Dashboard Section",
            "title": "Payments Disbursed",
            "showAllSimultaneously": false,
            "filters": [
              ...(config.frequency.kind === 'One Time Payment' ? [
                {
                  "name": "Grant Payment",
                  "filter": {
                    "field": "grant_payment",
                    "kind": "Exists"
                  } as FieldExists
                }
              ] : []),
              ...(config.frequency.kind === 'Recurring Payment' ? Array.from({ length: config.frequency.numberOfPayments }, (_, index) => (
                {
                  "name": `Payment ${index + 1}`,
                  "filter": {
                    "field": `payment_${index + 1}`,
                    "kind": "Exists"
                  } as FieldExists
                }
              )) : [])
            ],
            "components": [
              {
                "kind": "Hero Number",
                "title": "Payment Disbursements",
                "summary": {
                  "kind": "Count"
                },
                "filter": {
                  "kind": "And",
                  "clauses": [
                    {
                      "field": "submitted",
                      "kind": "Exists"
                    },
                    {
                      "field": "enrollment_decision",
                      "kind": "Equals",
                      "value": "yes"
                    }
                  ]
                }
              }
            ]
          },
          {
            "kind": "Hero Number",
            "title": "Total Funds Disbursed",
            "summary": {
              "kind": "Sum",
              "value": {
                "kind": "When",
                "when": [
                  {
                    "then": {
                      "kind": "NumericValue",
                      "value": 920
                    },
                    "cond": {
                      "kind": "And",
                      "clauses": [
                        {
                          "kind": "Or",
                          "clauses": [
                            ...(config.frequency.kind === 'One Time Payment' ? [
                              {
                                "field": "grant_payment",
                                "kind": "Exists"
                              } as FieldExists
                            ] : []),
                            ...(config.frequency.kind === 'Recurring Payment' ? Array.from({ length: config.frequency.numberOfPayments }, (_, index) => (
                              {
                                "field": `payment_${index + 1}`,
                                "kind": "Exists"

                              } as FieldExists
                            )) : [])
                          ]
                        }
                      ]
                    }
                  }
                ],
                "otherwise": {
                  "kind": "NumericValue",
                  "value": 0
                }
              }
            },
            "prefix": "$"
          },
          {
            "kind": "Hero Number",
            "title": "% Completed final payment disbursement",
            "summary": {
              "kind": "Multiply",
              "baseValue": {
                "kind": "NumericValue",
                "value": 100
              },
              "secondValue": {
                "kind": "Divide",
                "baseValue": {
                  "kind": "Sum",
                  "value": {
                    "kind": "When",
                    "when": [
                      {
                        "then": {
                          "kind": "NumericValue",
                          "value": 1
                        },
                        "cond": {
                          "kind": "And",
                          "clauses": [
                            ...(config.frequency.kind === "One Time Payment") ? [{
                              "field": "grant_payment",
                              "kind": "Exists"
                            } as FieldExists] : [],
                            ...(config.frequency.kind === "Recurring Payment") ? [{
                              "field": `payment_${config.frequency.numberOfPayments}`,
                              "kind": "Exists"
                            } as FieldExists] : [],
                          ]
                        }
                      }
                    ],
                    "otherwise": {
                      "kind": "NumericValue",
                      "value": 0
                    }
                  }
                },
                "secondValue": {
                  "kind": "Sum",
                  "value": {
                    "kind": "When",
                    "when": [
                      {
                        "cond": {
                          "kind": "And",
                          "clauses": [
                            {
                              "field": "submitted",
                              "kind": "Exists"
                            },
                            {
                              "field": "enrollment_decision",
                              "kind": "Equals",
                              "value": "yes"
                            }
                          ]
                        },
                        "then": {
                          "kind": "NumericValue",
                          "value": 1
                        }
                      }
                    ],
                    "otherwise": {
                      "kind": "NumericValue",
                      "value": 0
                    }
                  }
                }
              }
            }
          },
          {
            "kind": "Hero Number",
            "title": "# Completed final payment disbursement",
            "summary": {
              "kind": "Count"
            },
            "filter": {
              "kind": "And",
              "clauses": [
                ...(config.frequency.kind === "One Time Payment") ? [{
                  "field": "grant_payment",
                  "kind": "Exists"
                } as FieldExists] : [],
                ...(config.frequency.kind === "Recurring Payment") ? [{
                  "field": `payment_${config.frequency.numberOfPayments}`,
                  "kind": "Exists"
                } as FieldExists] : [],
              ]
            }
          },
          {
            "kind": "Dashboard Explanation",
            "content": {
              "en": "### Payments Initiated (by payment number)",
            }
          },
          {
            "kind": "Applicant Table",
            "columns": [
              {
                "kind": "Field",
                "field": "legal_name"
              },
              {
                "kind": "Field",
                "field": "actual_name"
              },
              {
                "kind": "Field",
                "field": "birth_date"
              },
              {
                "kind": "Field",
                "field": "payment_group"
              },
              {
                "kind": "Field",
                "field": "submitted"
              },
              {
                "kind": "Field",
                "field": "enrollment_yes_date"
              },
              {
                "kind": "Field",
                "field": "assignment"
              },
              {
                "kind": "Field",
                "field": "applicant_payment_type"
              },
              {
                "kind": "Field",
                "field": "grant_amount"
              },
              {
                "kind": "Named Expression",
                "name": {
                  "en": "Payment Status",
                },
                "expression": {
                  "kind": "Field",
                  "field": "payment_status"
                }
              },
              {
                "kind": "Named Expression",
                "expression": {
                  "kind": "Multiply",
                  "baseValue": {
                    "kind": "Field",
                    "field": "grant_amount"
                  },
                  "secondValue": {
                    "kind": "When",
                    "when": [
                      ...(config.frequency.kind === "One Time Payment") ? [{
                        "cond": {
                          "field": "payment_status",
                          "kind": "Equals",
                          "value": "grant_payment_paid"
                        },
                        "then": {
                          "kind": "NumericValue",
                          "value": 1
                        }
                      }] : [],
                      ...(config.frequency.kind === "Recurring Payment") ? Array.from({ length: config.frequency.numberOfPayments }, (_, index) => (
                        {
                          "cond": {
                            "field": "payment_status",
                            "kind": "Equals",
                            "value": `payment_${index + 1}_paid`
                          },
                          "then": {
                            "kind": "NumericValue",
                            "value": (index + 1)
                          }
                        }
                      )) : [],
                    ],
                    "otherwise": {
                      "kind": "NumericValue",
                      "value": 0
                    }
                  } as When
                },
                "name": {
                  "en": "Applicant's Cumulative Grant Amount To Date"
                }
              },
              ...(config.frequency.kind === 'One Time Payment') ? [{ "kind": "Field", "field": "grant_payment" } as Field] : [],
              ...(config.frequency.kind === 'Recurring Payment') ? Array.from({ length: config.frequency.numberOfPayments }, (_, index) => (
                {
                  "kind": "Field",
                  "field": `payment_${index + 1}`
                } as Field
              )) : [],
            ],
            "filter": {
              "kind": "And",
              "clauses": [
                {
                  "kind": "Or",
                  "clauses": [
                    {
                      "field": "enrollment_status",
                      "kind": "Equals",
                      "value": "treatment_enrolled"
                    },
                    {
                      "field": "enrollment_status",
                      "kind": "Equals",
                      "value": "control_enrolled"
                    }
                  ]
                },
                {
                  "field": "payment_group",
                  "kind": "Exists"
                }
              ]
            },
            "download": {
              "filename": "payments_initiated"
            },
            "title": {
              "en": "Payments Initiated by payment number",
            }
          },
          ...(config.ach.enabled) ? generateACHDashboards(config) : [],
          ...(config.debit.kind !== "None") ? generateDebitDashboards(config) : []
        ],
        "version": 1
      }
    ]
  };
};

const generateACHDashboards = (config: PaymentModule): DashboardComponent[] => {
  return [{
    "kind": "Dashboard Explanation",
    "content": {
      "en": "This table has a list of applicants who have pending or failed ACH payments. Please note: Pending payments don't necessarily require action at this time. You will see an increase in pending payments on payment dates. \n\nApplicant's/ Participant's who have been marked as \"corrections requested\" are in the additional table below. "
    }
  },
  {
    "kind": "Custom Query",
    "sql": "WITH LatestActions AS (\n    SELECT \n        SPLIT_PART(dgrant.reference, ':', 2) AS uid,\n        MAX(action.created) AS latest_action_created\n    FROM\n        rc_public.dwolla_float_ach_grant AS dgrant\n    LEFT JOIN \n        rc_public.action AS action ON dgrant.action_id = action.id\n    GROUP BY \n        SPLIT_PART(dgrant.reference, ':', 2)\n)\nSELECT \n    SPLIT_PART(dgrant.reference, ':', 2) AS uid,\n    CONCAT('$', ROUND(dgrant.grant_amount / 100.0)) AS amount_in_dollars,\n    action.created AS latest_action_created,\n    action.state AS action_state,\n    SPLIT_PART(dgrant.reference, ':', 3) AS payment_reference,\n    transaction.dwolla_data->>'status' AS dwolla_transaction_status\nFROM \n    rc_public.action AS action\nLEFT JOIN \n    rc_public.dwolla_float_ach_grant AS dgrant ON dgrant.action_id = action.id\nLEFT JOIN \n    rc_public.dwolla_transaction AS transaction ON transaction.transaction_id::uuid = dgrant.issued_transaction_id::uuid\nJOIN \n    LatestActions ON LatestActions.uid = SPLIT_PART(dgrant.reference, ':', 2)\nWHERE \n    action.state != 'COMPLETE'\n    AND (transaction.dwolla_data->>'status' != 'processed' OR transaction.dwolla_data->>'status' IS NULL)\n    AND action.created = LatestActions.latest_action_created;",
    "visualization": {
      "kind": "Table",
      "title": {
        "en": "ACH: Pending and/or Failed Payments"
      }
    }
  },
  {
    "kind": "Dashboard Explanation",
    "content": {
      "en": "The below table shows applicants who have had a payment attempted for them, but are now in a \"correction requested\" state. If you open the application, you will see an explanation in red on the landing page of their application.  \n\nYou can press \"mark resolved\" if the person has provided updated payment information and is not listed in the table above. "
    }
  },
  {
    "kind": "Applicant Table",
    "columns": [
      {
        "kind": "Field",
        "field": "legal_name"
      },
      {
        "kind": "Field",
        "field": "payment_group"
      },
      {
        "kind": "Field",
        "field": "applicant_payment_type"
      },
      {
        "kind": "Field",
        "field": "bank_account_name"
      }
    ],
    "title": {
      "en": "ACH: Correction Requested State",
    },
    "filter": {
      "kind": "And",
      "clauses": [
        {
          "field": "stage",
          "kind": "Equals",
          "value": "Correction Requested"
        }
      ]
    }
  },
  {
    "kind": "Dashboard Explanation",
    "content": {
      "en": "The table below shows which applicants have legal names that don't match the bank account name OR they have missing Bank Account Information. Please follow up with these applicants and confirm the information they have provided is true. if you've confirmed it's all correct, please go to \"ACH Payments\" on the drop down menu of their application, and click on the \"Admin Manual Override\". This table will include applicants no matter their payment group. "
    }
  },
  {
    "kind": "Applicant Table",
    "columns": [
      {
        "kind": "Field",
        "field": "legal_name"
      },
      {
        "kind": "Field",
        "field": "applicant_payment_type"
      },
      {
        "kind": "Field",
        "field": "bank_account_name"
      }
    ],
    "title": {
      "en": "ACH: Applicant Name does not match name on bank account OR is missing bank account information",
    },
    "filter": {
      "kind": "And",
      "clauses": [
        {
          "field": "applicant_payment_type",
          "kind": "Equals",
          "value": "ach"
        },
        {
          "kind": "Or",
          "clauses": [
            {
              "kind": "Value Less Than",
              "value": {
                "kind": "Field",
                "field": "bank_account_name_score"
              },
              "lessThan": {
                "kind": "StringValue",
                "value": "0.4"
              }
            },
            {
              "field": "bank_routing_number",
              "kind": "DoesntExist"
            },
            {
              "field": "bank_account_number",
              "kind": "DoesntExist"
            }
          ]
        },
        {
          "field": "bank_account_name_override",
          "kind": "DoesntExist"
        }
      ]
    }
  }];
};

const generateDebitDashboards = (config: PaymentModule): DashboardComponent[] => {
  return [
    ...(config.debit.kind === 'Usio') ? [
      {
        "kind": "Dashboard Explanation",
        "content": {
          "en": "This table has a list of applicants who have a debit card payment that is pending or failed. For pending- please click on “View” on the right side of the page to go to the applicant’s profile. Payments will always be pending if a card is not activated. "
        }
      } as DashboardExplanation,
      {
        "kind": "Custom Query",
        "sql": "WITH LatestActions AS (\n    SELECT \n        SPLIT_PART(usio.reference, ':', 2) AS uid,\n        MAX(action.created) AS latest_action_created\n    FROM\n        rc_public.load_usio_card AS usio\n    LEFT JOIN \n        rc_public.action AS action ON usio.action_id = action.id\n    GROUP BY \n        SPLIT_PART(usio.reference, ':', 2)\n)\nSELECT \n    SPLIT_PART(usio.reference, ':', 2) AS uid,\n    CONCAT('$', ROUND(usio.grant_amount / 100.0)) AS amount_in_dollars,\n    action.created AS latest_action_created,\n    action.state AS action_state,\n    SPLIT_PART(usio.reference, ':', 3) AS payment_reference\nFROM \n    rc_public.action AS action\nLEFT JOIN \n    rc_public.load_usio_card AS usio ON usio.action_id = action.id\nJOIN \n    LatestActions ON LatestActions.uid = SPLIT_PART(usio.reference, ':', 2)\nWHERE \n    action.state != 'COMPLETE'\n   AND action.created = LatestActions.latest_action_created;",
        "visualization": {
          "kind": "Table",
          "title": {
            "en": "Debit Card: Pending and/or Failed Payments - Note: pending most likely means the card is not activated or just had a load sent."
          }
        }
      } as CustomQuery,
    ] : [],
    ...(config.debit.kind !== "None" && config.debit.pickup) ? [
      {
        "kind": "Dashboard Explanation",
        "content": {
          "en": "This table has a list of individuals who need to pick up a debit card. "
        }
      } as DashboardExplanation,
      {
        "kind": "Applicant Table",
        "columns": [
          {
            "kind": "Field",
            "field": "actual_name"
          },
          {
            "kind": "Field",
            "field": "assignment"
          },
          {
            "kind": "Field",
            "field": "enrollment_status"
          },
          {
            "kind": "Field",
            "field": "payment_group"
          },
          {
            "kind": "Field",
            "field": "first_payment_date"
          }
        ],
        "filter": {
          "kind": "And",
          "clauses": [
            {
              "kind": "Or",
              "clauses": [
                ...(config.debit.kind === 'GiveCard') ? [{
                  "field": "applicant_payment_type",
                  "kind": "Equals",
                  "value": "givecard_load"
                } as FieldEquals] : [],
                ...(config.debit.kind === 'Usio') ? [{
                  "field": "applicant_payment_type",
                  "kind": "Equals",
                  "value": "usio_pickup"
                } as FieldEquals] : [],
              ]
            },
            {
              "field": "dc_confirm_pickup",
              "kind": "DoesntExist"
            },
            {
              "field": "card_id",
              "kind": "DoesntExist"
            }
          ]
        },
        "title": {
          "en": "Debit Card Pick Up Needed"
        }
      } as ApplicantTable
    ] : []
  ]
};